/**
 * @fileoverview Mutation hook for My Page's Send Payment Details command.
 * @description Sends the payment details through `useIdempotentMutation`, which keeps the
 * command's `Idempotency-Key` in a registry on the QueryClient: a retry after a timeout or a 5xx,
 * even from a dialog that was closed and reopened, replays the first request instead of emailing
 * the bank a second time. The key is scoped to the target and the chosen contact.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { sendMyPagePaymentDetails } from '@/features/sales/services/my-page-targets.service'

import type { MyPagePaymentRecipient } from '@/features/sales/types/my-page.types'

interface SendPaymentVariables {
  targetId: string
  recipient: MyPagePaymentRecipient
}

/**
 * @description Sends the payment details under a kept key, then refreshes every sales query so the
 * table, KPIs and details show the target's new state.
 * @param {() => void} [onSuccess] - Called after a success, even when the dialog has unmounted.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the target id and
 * the bank contact to email).
 */
export function useSendMyPagePaymentDetails(onSuccess?: () => void) {
  return useIdempotentMutation({
    action: 'send-my-page-payment-details',
    /**
     * @description Sends the command with the key it is given.
     * @param {SendPaymentVariables} variables - The target and the contact to email.
     * @param {string} idempotencyKey - The key kept for this command.
     * @returns {Promise<MyPagePaymentDetailsSent>} The target's payment state after the send.
     */
    send: ({ targetId, recipient }: SendPaymentVariables, idempotencyKey: string) =>
      sendMyPagePaymentDetails(targetId, recipient, idempotencyKey),
    onSuccess,
  })
}
